// utils/feeOverride.ts
//
// FEE-RATE-SEMANTICS-1 Phase 2B -- strict parsing of a user-entered manual fee
// override (SaleDetail "Override", SaleEntry manual Fee edit).
//
// Condition 4a (binding): override validation MUST use a full numeric
// conversion (Number) with finite + non-negative checks, NOT parseFloat --
// parseFloat("1abc") silently yields 1, letting a malformed override serialize
// as a real fee. Invalid or negative input must block BEFORE payload
// construction and must never serialize NaN as JSON null.

export type FeeOverrideParse =
  | { ok: true; value: number }
  | { ok: false; reason: "blank" | "invalid" };

/**
 * Parse a raw override string into a validated non-negative fee.
 *
 * - blank / whitespace-only -> { ok:false, reason:"blank" } (caller treats a
 *   blank override as "no override" == configured intent; this is NOT an error
 *   the user must fix, just an absence).
 * - non-finite, non-numeric ("1abc", "", "NaN", "Infinity"), or negative
 *   -> { ok:false, reason:"invalid" } (blocks before the network).
 * - a finite, >= 0 number (including 0) -> { ok:true, value }.
 *
 * Uses Number(trimmed), never parseFloat: Number("1abc") is NaN (rejected),
 * whereas parseFloat("1abc") is 1 (wrongly accepted).
 */
export function parseFeeOverride(raw: string | null | undefined): FeeOverrideParse {
  const trimmed = String(raw ?? "").trim();
  if (trimmed === "") return { ok: false, reason: "blank" };
  // Number() (not parseFloat): Number("1abc") === NaN, rejected here; whereas
  // parseFloat("1abc") === 1 would wrongly pass. Number("") is 0, but the blank
  // guard above already returned, so an empty override never reaches this.
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return { ok: false, reason: "invalid" };
  return { ok: true, value: n };
}
